"use client";

import { Command, CommandGroup, CommandItem, CommandList } from "@/components/ui/command";
import type { Edge, Node } from "@xyflow/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { computeNodeStepNumbers, extractWorkflowVariables, isValidVariable, resolveVariableValue } from "./extract-workflow-variables";
import { useRunExecutionStore } from "@/stores/runExecutionStore";
import { createVariableSpan, getCurrentWordInContentEditable, getVariableBeforeCursor, insertVariableAtCursor, serializeContentEditable, ZERO_WIDTH_SPACE } from "./variable-textarea-utils";
import { useInputSchema } from "./InputSchemaContext";
interface NodeDefinition {
  type: string;
  resultSchema?: {
    properties?: Record<string, {
      description?: string;
      type?: string;
    }>;
  };
}
interface ColorInputProps {
  label?: string;
  description?: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  placeholder?: string;
  currentNodeId?: string;
  allNodes?: Node[];
  allEdges?: Edge[];
  nodeDefinitions?: NodeDefinition[];
}

/**
 * Check if a string is a valid hex color (not a variable)
 */
const isValidHexColor = (val: string): boolean => {
  if (!val || typeof val !== "string") return false;
  return /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/.test(val);
};

/**
 * Normalize value to 6-digit hex for the color picker
 */
const normalizeToSixDigit = (hex: string): string => {
  if (!hex || !hex.startsWith("#")) return "#000000";
  const clean = hex.slice(1);
  if (clean.length === 3) {
    return `#${clean[0]}${clean[0]}${clean[1]}${clean[1]}${clean[2]}${clean[2]}`;
  }
  if (clean.length === 6 || clean.length === 8) {
    return `#${clean.slice(0, 6)}`;
  }
  return "#000000";
};

/**
 * Color input with a native color picker and variable-aware text input
 * Supports #RGB, #RRGGBB formats and {{variable}} syntax
 */
export default function ColorInput({
  label,
  description,
  value,
  onChange,
  required = false,
  placeholder = "#000000 or {{variable}}",
  currentNodeId = "",
  allNodes = [],
  allEdges = [],
  nodeDefinitions = []
}: ColorInputProps) {
  const inputSchema = useInputSchema();
  const nodeStates = useRunExecutionStore(state => state.currentRun?.nodeStates);
  const editableRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [dropdownPlacement, setDropdownPlacement] = useState<"above" | "below">("below");
  const [currentTrigger, setCurrentTrigger] = useState("");
  const isUpdatingRef = useRef(false);
  const lastValueRef = useRef(value);

  // Check if current value is a valid hex color (for showing color picker)
  const showColorPicker = isValidHexColor(value) || !value;
  const colorPickerValue = isValidHexColor(value) ? normalizeToSixDigit(value) : "#000000";

  // Compute step numbers for display labels (BFS from root)
  const stepNumbers = useMemo(() => computeNodeStepNumbers(allNodes, allEdges), [allNodes, allEdges]);

  // Extract available variables from workflow
  const availableVariables = useMemo(() => {
    if (!currentNodeId || allNodes.length === 0) {
      return [];
    }
    return extractWorkflowVariables(currentNodeId, allNodes, allEdges, nodeDefinitions, undefined, inputSchema, stepNumbers);
  }, [currentNodeId, allNodes, allEdges, nodeDefinitions, inputSchema, stepNumbers]);

  // Build lookup: variableId -> displayLabel
  const displayLabelMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const v of availableVariables) {
      if (v.displayLabel) map.set(v.id, v.displayLabel);
    }
    return map;
  }, [availableVariables]);

  // Callback to get display text for a variable (friendly name + optional run value)
  const getDisplayText = useCallback((variableId: string): string | undefined => {
    const label_0 = displayLabelMap.get(variableId);
    if (!label_0) return undefined;
    const runVal = resolveVariableValue(variableId, nodeStates);
    return runVal != null ? `${label_0} = ${runVal}` : label_0;
  }, [displayLabelMap, nodeStates]);

  // Custom parser for single-line input
  const parseStringToFragmentSingleLine = useCallback((val: string) => {
    const fragment = document.createDocumentFragment();
    const variableRegex = /({{[^}]+}})/g;
    const parts = val.split(variableRegex);
    let lastNodeWasVariable = false;
    parts.forEach(part => {
      if (!part) return;
      if (part.startsWith("{{") && part.endsWith("}}")) {
        const isValid = isValidVariable(part, availableVariables);
        const displayText = getDisplayText(part);
        const span = createVariableSpan(part, isValid, displayText);
        fragment.appendChild(span);
        lastNodeWasVariable = true;
      } else {
        const cleanPart = part.replace(/\n/g, " ");
        if (cleanPart) {
          fragment.appendChild(document.createTextNode(cleanPart));
          lastNodeWasVariable = false;
        }
      }
    });
    if (lastNodeWasVariable) {
      fragment.appendChild(document.createTextNode(ZERO_WIDTH_SPACE));
    }
    return fragment;
  }, [availableVariables, getDisplayText]);

  // Sync DOM with value prop
  useEffect(() => {
    if (!editableRef.current || isUpdatingRef.current) return;
    const currentContent = serializeContentEditable(editableRef.current);
    if (currentContent === value) {
      lastValueRef.current = value;
      return;
    }
    editableRef.current.innerHTML = "";
    const fragment_0 = parseStringToFragmentSingleLine(value);
    editableRef.current.appendChild(fragment_0);
    lastValueRef.current = value;
  }, [value, parseStringToFragmentSingleLine]);

  // Refresh pill display text when run values change (nodeStates updates)
  useEffect(() => {
    if (!editableRef.current) return;
    const pills = editableRef.current.querySelectorAll(`[data-variable]`);
    pills.forEach(pill => {
      const varId = pill.getAttribute("data-variable");
      if (varId) {
        const newText = getDisplayText(varId) || varId;
        // Update the text span child (first child), not the whole pill
        const textSpan = pill.querySelector(".variable-text");
        const target = textSpan || pill;
        if (target.textContent !== newText) {
          target.textContent = newText;
        }
      }
    });
  }, [getDisplayText]);

  // Handle input changes
  const handleInput = useCallback(() => {
    if (!editableRef.current) return;
    isUpdatingRef.current = true;
    const newValue = serializeContentEditable(editableRef.current);
    if (newValue !== lastValueRef.current) {
      lastValueRef.current = newValue;
      onChange(newValue);
    }

    // Check for variable trigger
    const {
      word
    } = getCurrentWordInContentEditable(editableRef.current);
    if (word.startsWith("{{")) {
      if (containerRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - containerRect.bottom;
        const spaceAbove = containerRect.top;
        const showAbove = spaceBelow < 220 && spaceAbove > 220;
        setDropdownPlacement(showAbove ? "above" : "below");
      }
      setDropdownVisible(true);
      setCurrentTrigger(word);
    } else {
      setDropdownVisible(false);
      setCurrentTrigger("");
    }
    isUpdatingRef.current = false;
  }, [onChange]);

  // Handle variable selection from dropdown
  const onVariableSelect = useCallback((variableId_0: string) => {
    if (!editableRef.current) return;
    const isValid_0 = isValidVariable(variableId_0, availableVariables);
    const displayText_0 = getDisplayText(variableId_0);
    insertVariableAtCursor(editableRef.current, variableId_0, isValid_0, displayText_0);
    setDropdownVisible(false);
    setCurrentTrigger("");
    const newValue_0 = serializeContentEditable(editableRef.current);
    lastValueRef.current = newValue_0;
    onChange(newValue_0);
    editableRef.current.focus();
  }, [onChange, availableVariables, getDisplayText]);

  // Handle keydown events
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      return;
    }
    if (e.key === "Backspace" && editableRef.current) {
      const variableSpan = getVariableBeforeCursor(editableRef.current);
      if (variableSpan) {
        e.preventDefault();
        const parent = variableSpan.parentNode;
        if (parent) {
          const range = document.createRange();
          const selection = window.getSelection();
          const nextSibling = variableSpan.nextSibling;
          if (nextSibling && nextSibling.nodeType === Node.TEXT_NODE && (nextSibling.textContent || "").replace(/[\u200B\s]/g, "") === "") {
            nextSibling.remove();
          }
          if (variableSpan.previousSibling) {
            range.setStartAfter(variableSpan.previousSibling);
          } else {
            range.setStart(parent, 0);
          }
          range.collapse(true);
          variableSpan.remove();
          selection?.removeAllRanges();
          selection?.addRange(range);
          const newValue_1 = serializeContentEditable(editableRef.current);
          lastValueRef.current = newValue_1;
          onChange(newValue_1);
        }
        return;
      }
    }
    if (dropdownVisible) {
      if (e.key === "Escape") {
        e.preventDefault();
        setDropdownVisible(false);
        setCurrentTrigger("");
      }
    }
  }, [dropdownVisible, onChange]);

  // Handle paste
  const handlePaste = useCallback((e_0: React.ClipboardEvent<HTMLDivElement>) => {
    e_0.preventDefault();
    let text = e_0.clipboardData.getData("text/plain");
    if (!text || !editableRef.current) return;
    text = text.replace(/\n/g, " ");
    const selection_0 = window.getSelection();
    if (!selection_0 || selection_0.rangeCount === 0) return;
    const range_0 = selection_0.getRangeAt(0);
    range_0.deleteContents();
    const fragment_1 = parseStringToFragmentSingleLine(text);
    range_0.insertNode(fragment_1);
    range_0.collapse(false);
    selection_0.removeAllRanges();
    selection_0.addRange(range_0);
    const newValue_2 = serializeContentEditable(editableRef.current);
    lastValueRef.current = newValue_2;
    onChange(newValue_2);
  }, [onChange, parseStringToFragmentSingleLine]);

  // Handle blur
  const handleBlur = useCallback(() => {
    setTimeout(() => {
      setDropdownVisible(false);
      setCurrentTrigger("");
    }, 200);
  }, []);

  // Prevent dropdown blur
  const handleDropdownMouseDown = useCallback((e_1: React.MouseEvent) => {
    e_1.preventDefault();
    e_1.stopPropagation();
  }, []);

  // Handle color picker change
  const handleColorPickerChange = (e_2: React.ChangeEvent<HTMLInputElement>) => {
    const newColor = e_2.target.value.toUpperCase();
    onChange(newColor);
  };

  // Filter variables for dropdown
  const searchQuery = currentTrigger.replace("{{", "").toLowerCase();
  const filteredVariables = availableVariables.filter(v_0 => v_0.id.toLowerCase().includes(searchQuery) || v_0.label.toLowerCase().includes(searchQuery) || v_0.displayLabel?.toLowerCase().includes(searchQuery));
  return <div className="space-y-2">
      {label && <label className="field-label block">
          {label}
          {required && <span className="ml-1 text-red-500">*</span>}
        </label>}
      {description && <p className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">{description}</p>}

      <div ref={containerRef} className="relative">
        <div className="flex items-center gap-2">
          {/* Color picker swatch - only functional when value is a valid hex color */}
          <div className="relative">
            <input type="color" value={colorPickerValue} onChange={handleColorPickerChange} className="absolute inset-0 h-full w-full cursor-pointer opacity-0" title="Pick a color" />
            <div className="border-ds-neutral-300 h-9 w-9 flex-shrink-0 cursor-pointer rounded-lg border-2 transition-colors hover:border-zinc-400 dark:border-zinc-600 dark:hover:border-zinc-500" style={{
            backgroundColor: showColorPicker ? colorPickerValue : "#808080"
          }} />
          </div>

          {/* Contenteditable input with variable support */}
          <div className="border-ds-neutral-300 dark:border-ds-neutral-700 bg-ds-surface-card shadow-ds-soft relative flex-1 overflow-hidden rounded-lg border focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/20">
            <div ref={editableRef} contentEditable suppressContentEditableWarning onInput={handleInput} onKeyDown={handleKeyDown} onPaste={handlePaste} onBlur={handleBlur} className="text-ds-text-primary empty:before:text-ds-text-disabled dark:empty:before:text-ds-text-muted w-full overflow-x-auto border-0 bg-transparent px-3 py-2 font-mono text-sm uppercase leading-relaxed caret-zinc-900 outline-none empty:before:normal-case empty:before:content-[attr(data-placeholder)] dark:text-zinc-100 dark:caret-zinc-100" data-placeholder={placeholder} style={{
            whiteSpace: "nowrap"
          }} />
          </div>
        </div>

        {/* Variable Suggestions Dropdown */}
        {dropdownVisible && filteredVariables.length > 0 && <div ref={dropdownRef} onMouseDown={handleDropdownMouseDown} className={`absolute left-0 right-0 z-50 ${dropdownPlacement === "above" ? "bottom-full mb-1" : "top-full mt-1"}`}>
            <Command className="border-ds-border-default bg-ds-surface-card max-h-[280px] w-full overflow-auto rounded-lg border shadow-xl">
              <CommandList>
                {Object.entries(filteredVariables.reduce((acc, v_1) => {
              if (!acc[v_1.category]) acc[v_1.category] = [];
              acc[v_1.category].push(v_1);
              return acc;
            }, {} as Record<string, typeof filteredVariables>)).map(([category, vars]) => <div key={category} className="border-ds-neutral-100 dark:border-ds-neutral-700/50 border-b last:border-0">
                    <div className="text-ds-text-secondary dark:bg-ds-neutral-900 sticky top-0 z-10 bg-zinc-50 px-3 py-2 text-xs font-semibold">
                      {category}
                    </div>
                    <CommandGroup>
                      {vars.map(variable => {
                  const runVal_0 = resolveVariableValue(variable.id, nodeStates);
                  return <CommandItem key={variable.id} value={variable.id} onSelect={() => onVariableSelect(variable.id)} className="cursor-pointer px-3 py-2">
                            <div className="flex flex-col gap-1">
                              <span className="rounded-ds-xs inline-flex items-center gap-1.5 bg-gradient-to-r from-blue-50 to-indigo-100 px-2 py-1 font-mono text-sm text-blue-700 dark:from-blue-950/30 dark:to-indigo-950/30 dark:text-blue-400">
                                {variable.displayLabel || variable.id}
                              </span>
                              <span className="label-xs">{variable.label}</span>
                              {runVal_0 != null && <span className="font-mono text-xs text-green-600 dark:text-green-400">= {runVal_0}</span>}
                            </div>
                          </CommandItem>;
                })}
                    </CommandGroup>
                  </div>)}
              </CommandList>
            </Command>
          </div>}
      </div>
    </div>;
}